// The investigation graph: what a chat has established so far, as typed
// anchors with provenance, kept outside the message history.
//
// Every analytics result feeds it: id columns become nodes (endpoint,
// session, process, prompt, message, person, window), co-occurring ids in
// a row become edges, and each write records the tool call it came from.
// Every node carries the evidence classes of the entities that produced
// it, because the same endpoint can be known from a directory row, a
// prompt summary, or a process start, and only some of those can carry
// an attribution. Leaf sub-agents return writes in the same shape and
// the parent merges them, so the graph is written by many hands and read
// by one context block.
//
// Pure and dependency-light: no analytics client, no React, so it runs
// in vitest and inside execute_python's bridge alike.

import { getScopeDimForColumn } from "./scopeColumns.js";
import { parseServerTimestamp } from "./traceUtils.js";

export const EVIDENCE = Object.freeze({
    EXECUTION: "execution",
    CONVERSATION: "conversation",
    PROMPT_SUMMARY: "prompt-summary",
    DIRECTORY: "directory",
    DERIVED: "derived",
});

export const NODE_KINDS = Object.freeze(["endpoint", "session", "process", "prompt", "message", "person", "window", "host"]);

// The rows of these entities say what ran. Everything the conversation
// capture holds says what the model said or called; prompt summaries say
// what an agent described; directory entities say who owns what.
const EXECUTION_ENTITIES = /^(PROCESS_EVENT|TELEMETRY_TIMELINE|DNS_EVENT|FILE_EVENT|NETWORK_CONNECTION|TRACE_TOOL_ACTIVITY|TRACE_OS_ROLLUP)$/;
const CONVERSATION_ENTITIES = /^(CONVERSATION_|TRACE_ACTIVATION|MODEL_INVOCATION)/;
const DIRECTORY_ENTITIES = /^(ENDPOINT|DIRECTORY_USER|ENDPOINT_USER_ASSIGNMENT|IDENTITY_OBSERVATION|ENDPOINT_STATUS|ENDPOINT_METADATA_CHANGE|AGENT_INVENTORY)$/;

export function evidenceClassFor(entity) {
    const name = String(entity || "").replace(/^ENTITY_TYPE_/, "");
    if (EXECUTION_ENTITIES.test(name)) return EVIDENCE.EXECUTION;
    if (CONVERSATION_ENTITIES.test(name)) return EVIDENCE.CONVERSATION;
    if (DIRECTORY_ENTITIES.test(name)) return EVIDENCE.DIRECTORY;
    return EVIDENCE.PROMPT_SUMMARY;
}

// Column name -> node kind. Builds on the scope-chip classifier, which
// already knows the id columns the dashboard deeplinks, and adds the
// investigation kinds the chips do not link (traces, processes, messages).
const COLUMN_KIND_OVERRIDES = [
    [/(^|_)trace_?id$/i, "session"],
    [/(^|_)session_?id$/i, "session"],
    [/(^|_)process_start_key$/i, "process"],
    [/(^|_)message_?id$/i, "message"],
    [/(^|_)prompt_?id$/i, "prompt"],
    [/(^|_)(registered_)?user(_?name|_?id)?$/i, "person"],
    [/(^|_)account_?name$/i, "person"],
    [/(^|_)(remote_address|remote_host|query_name|dns_query_name)$/i, "host"],
];
const DIM_TO_KIND = { endpoints: "endpoint", sessions: "session" };

export function nodeKindForColumn(column) {
    const raw = String(column || "");
    const tail = raw.includes("__") ? raw.slice(raw.lastIndexOf("__") + 2) : raw;
    for (const [re, kind] of COLUMN_KIND_OVERRIDES) if (re.test(tail)) return kind;
    const dim = getScopeDimForColumn(raw);
    return dim ? DIM_TO_KIND[dim] || null : null;
}

const LABEL_COLUMNS = {
    endpoint: [/hostname$/i, /endpoint_name$/i, /^host$/i],
    session: [/provider$/i, /client_surface$/i, /model$/i],
    process: [/command_line$/i, /image_name$/i],
    prompt: [/summary$/i],
    person: [/display_name$/i, /email$/i],
};

// Trace ids, session ids, and notification ids are different id spaces
// that all read as "session" to a person; a pivot must match the right one.
export function sessionIdKind(column) {
    const tail = String(column || "").toLowerCase().replace(/^.*__/, "");
    if (/trace_?id$/.test(tail)) return "trace";
    if (/notification_?id$/.test(tail)) return "notification";
    return "session";
}

export function nodeKey(kind, id) {
    return `${kind}:${String(id)}`;
}

export function createGraph() {
    return { nodes: new Map(), edges: new Map(), claims: [], writes: 0 };
}

function edgeKey(from, to, kind) {
    return `${from}|${kind}|${to}`;
}

// Merge one write set into the graph. Idempotent by key: a node seen
// twice keeps one record with the union of its evidence and both sources.
export function applyWrites(graph, writes) {
    const applied = { nodes: 0, edges: 0, claims: 0 };
    for (const [place, n] of (writes?.nodes || []).entries()) {
        if (!n?.kind || n.id == null || n.id === "") continue;
        const key = nodeKey(n.kind, n.id);
        const existing = graph.nodes.get(key);
        const source = n.source ? { ...n.source } : null;
        if (existing) {
            for (const e of n.evidence || []) existing.evidence.add(e);
            if (source && !existing.sources.some((s) => s.toolCallId === source.toolCallId && s.entity === source.entity)) {
                existing.sources.push(source);
                // The first is the call that brought it in; the rest are the
                // latest sightings.
                if (existing.sources.length > 8) existing.sources.splice(1, 1);
            }
            if (n.label && !existing.label) existing.label = n.label;
            mergeAttrs(existing.attrs, n.attrs);
            noteSpan(existing, graph.writes);
            noteNamed(existing, graph.writes, place);
        } else {
            const node = {
                key,
                kind: n.kind,
                id: String(n.id),
                label: n.label || null,
                evidence: new Set(n.evidence || []),
                sources: source ? [source] : [],
                attrs: { ...(n.attrs || {}) },
                // Which write first brought it in: the investigation's own
                // clock (one tick per write set, and per claim made on its
                // own), which a tool call id does not carry.
                seq: graph.writes,
            };
            noteSpan(node, graph.writes);
            noteNamed(node, graph.writes, place);
            graph.nodes.set(key, node);
            applied.nodes++;
        }
    }
    for (const e of writes?.edges || []) {
        if (!e?.from || !e?.to || !e?.kind) continue;
        const key = edgeKey(e.from, e.to, e.kind);
        const existing = graph.edges.get(key);
        if (existing) {
            for (const ev of e.evidence || []) existing.evidence.add(ev);
            if (e.source && !existing.sources.some((s) => s.toolCallId === e.source.toolCallId)) existing.sources.push({ ...e.source });
        } else {
            graph.edges.set(key, { key, from: e.from, to: e.to, kind: e.kind, evidence: new Set(e.evidence || []), sources: e.source ? [{ ...e.source }] : [], seq: graph.writes });
            applied.edges++;
        }
    }
    for (const c of writes?.claims || []) {
        const result = recordClaim(graph, c);
        if (result.added) applied.claims++;
    }
    graph.writes += 1;
    return applied;
}

// A second row about the same process can be a later event of it (a file it
// wrote, a connection it made), so the earliest time is the one that dates
// its start; a longer command line is the same line cut less.
function mergeAttrs(into, attrs) {
    for (const [k, v] of Object.entries(attrs || {})) {
        if (k === "at" && Number.isFinite(into.at)) into.at = Number.isFinite(v) ? Math.min(into.at, v) : into.at;
        else if (k === "last" && Number.isFinite(into.last)) into.last = Number.isFinite(v) ? Math.max(into.last, v) : into.last;
        else if (k === "command" && typeof into.command === "string" && into.command.length >= String(v).length) continue;
        else into[k] = v;
    }
}

// A session's span as each write left it ([seq, at, last]), so a reader
// stepping back through the investigation sees the span known at that step
// rather than the one later rows widened it to. Bounded: dropping a middle
// entry only shows that step the narrower span before it.
const SPAN_HISTORY = 12;

function noteSpan(node, seq) {
    if (node.kind !== "session" || !Number.isFinite(node.attrs.at)) return;
    const spans = node.spans || (node.spans = []);
    const prev = spans[spans.length - 1];
    if (prev && prev[1] === node.attrs.at && prev[2] === node.attrs.last) return;
    spans.push([seq, node.attrs.at, node.attrs.last]);
    if (spans.length > SPAN_HISTORY) spans.splice(1, 1);
}

// The latest write that named a session and its place among that write's
// nodes, which come in the order the query returned its rows ([seq, place]):
// before the chat claims anything or reads into a trace, the traces its latest
// queries named are the ones it is looking at (graphTraceSessions).
function noteNamed(node, seq, place) {
    if (node.kind === "session") node.named = [seq, place];
}

// Labels are cut here without a mark, so a label this long may not be whole.
export const LABEL_MAX = 160;

function pickLabel(kind, row) {
    for (const re of LABEL_COLUMNS[kind] || []) {
        for (const [col, value] of Object.entries(row)) {
            if (re.test(col) && typeof value === "string" && value) return value.slice(0, LABEL_MAX);
        }
    }
    return null;
}

// What placing a process on a trace's turns needs beyond its label: the whole
// command line when the label had to cut it (a harness-wrapped command is
// mostly wrapper, and the command the agent ran sits past the cut), and the
// row's time, which bounds the calls that could have started it.
const COMMAND_MAX = 2000;
const TIME_COLUMN = /(^|_)(timestamp|time|ts|at)$/i;

// The time a row happened at, from its first time column.
function rowTime(row) {
    for (const [col, value] of Object.entries(row)) {
        const tail = col.includes("__") ? col.slice(col.lastIndexOf("__") + 2) : col;
        if (TIME_COLUMN.test(tail) && !/create_?time$/i.test(tail) && typeof value === "string") {
            const ms = parseServerTimestamp(value);
            if (Number.isFinite(ms)) return ms;
        }
    }
    return null;
}

function processAttrs(row) {
    const attrs = {};
    for (const [col, value] of Object.entries(row)) {
        const tail = col.includes("__") ? col.slice(col.lastIndexOf("__") + 2) : col;
        if (attrs.command == null && /command_line$/i.test(tail) && typeof value === "string" && value.length > LABEL_MAX) {
            attrs.command = value.slice(0, COMMAND_MAX);
        }
    }
    const at = rowTime(row);
    if (at != null) attrs.at = at;
    return attrs;
}

// The kinds that happen at a time: a message or a prompt when its row was
// written, a session over the span its rows cover (earliest and latest seen).
// Endpoints and people are not events; their time is their sessions'.
const DATED_KINDS = new Set(["process", "message", "prompt", "session"]);

function datedAttrs(kind, row, attrs) {
    if (!DATED_KINDS.has(kind)) return attrs;
    const at = attrs.at ?? rowTime(row);
    return at == null ? attrs : { ...attrs, at, last: at };
}

// Turn one analytics result into writes. `entity` is the spec's root
// entity, which decides the evidence class of everything in the rows.
// Rows are plain objects (analyticsClient.rowsToObjects). Cap rows so a
// 1000-row pull does not become 1000 nodes; the store keeps the rest.
export function extractWrites({ toolCallId, entity, rows, timeRange = null, maxRows = 200 }) {
    const evidence = evidenceClassFor(entity);
    const source = { toolCallId: toolCallId || null, entity: String(entity || "").replace(/^ENTITY_TYPE_/, "") || null };
    const nodes = [];
    const edges = [];
    const seen = new Map();
    const list = Array.isArray(rows) ? rows.slice(0, maxRows) : [];
    for (const row of list) {
        const anchors = [];
        for (const [col, value] of Object.entries(row)) {
            if (value == null || value === "" || typeof value === "object") continue;
            const kind = nodeKindForColumn(col);
            if (!kind) continue;
            const id = String(value);
            if (kind === "person" && !/^[a-z0-9._@-]{2,64}$/i.test(id)) continue;
            const key = nodeKey(kind, id);
            anchors.push({ kind, id, key });
            if (!seen.has(key)) {
                const attrs = kind === "session" ? { idKind: sessionIdKind(col) } : kind === "process" ? processAttrs(row) : {};
                const node = { kind, id, label: pickLabel(kind, row), evidence: [evidence], source, attrs: datedAttrs(kind, row, attrs) };
                seen.set(key, node);
                nodes.push(node);
            } else if (DATED_KINDS.has(kind)) {
                // A later row of the same result widens the span it covers.
                mergeAttrs(seen.get(key).attrs, datedAttrs(kind, row, {}));
            }
        }
        // Co-occurrence in one row is a relation the row asserts.
        for (let i = 0; i < anchors.length; i++) {
            for (let j = 0; j < anchors.length; j++) {
                if (i === j || anchors[i].kind === anchors[j].kind) continue;
                const rel = relationFor(anchors[i].kind, anchors[j].kind);
                if (!rel) continue;
                edges.push({ from: anchors[i].key, to: anchors[j].key, kind: rel, evidence: [evidence], source });
            }
        }
    }
    if (timeRange?.absolute?.start && timeRange?.absolute?.end) {
        const id = `${timeRange.absolute.start}/${timeRange.absolute.end}`;
        nodes.push({ kind: "window", id, evidence: [EVIDENCE.DERIVED], source });
        for (const n of nodes) {
            if (n.kind === "window" || n.kind === "person" || n.kind === "host") continue;
            edges.push({ from: nodeKey(n.kind, n.id), to: nodeKey("window", id), kind: "observed_in", evidence: [evidence], source });
        }
    }
    return { nodes, edges, claims: [] };
}

const RELATIONS = {
    "process>session": "in_session",
    "process>endpoint": "ran_on",
    "process>person": "ran_as",
    "session>endpoint": "on_endpoint",
    "session>person": "attributed_to",
    "prompt>session": "in_session",
    "prompt>endpoint": "on_endpoint",
    "message>session": "in_session",
    "endpoint>person": "registered_to",
    "process>host": "reached",
    "session>host": "reached",
};

export function relationFor(fromKind, toKind) {
    return RELATIONS[`${fromKind}>${toKind}`] || null;
}

// Claims are conclusions with cited nodes. The gate is the point: an
// attribution (who ran X, on which machine, in which session) needs at
// least one cited node that an execution record produced, or a
// conversation tool-use record of the model's own call. Prompt summaries
// and directory rows alone say intent and ownership, not what ran.
export const CLAIM_KINDS = Object.freeze(["attribution", "activity", "presence", "finding"]);
const ATTRIBUTION_EVIDENCE = new Set([EVIDENCE.EXECUTION, EVIDENCE.CONVERSATION]);

export function evaluateClaim(graph, claim) {
    const cited = (claim.evidence || []).map((k) => graph.nodes.get(k)).filter(Boolean);
    const missing = (claim.evidence || []).filter((k) => !graph.nodes.has(k));
    if (!cited.length) {
        if (!(claim.evidence || []).length) return { ok: false, reason: "no evidence keys given; cite anchor keys from graph_state or the last query's _graph delta" };
        return { ok: false, reason: "cites no known node" + (missing.length ? ` (unknown: ${missing.join(", ")})` : "") };
    }
    if (claim.kind === "attribution") {
        const classes = new Set();
        for (const n of cited) for (const e of n.evidence) classes.add(e);
        const hasExecution = [...classes].some((c) => ATTRIBUTION_EVIDENCE.has(c));
        if (!hasExecution) {
            return {
                ok: false,
                reason: `attribution needs an execution record (PROCESS_EVENT, TELEMETRY_TIMELINE, TRACE_TOOL_ACTIVITY) or the model's own tool call (CONVERSATION_SEARCH_CONTENT tool_use); cited evidence is ${[...classes].join(", ") || "none"}`,
            };
        }
    }
    return { ok: true, reason: null };
}

export function addClaim(graph, claim) {
    const result = recordClaim(graph, claim);
    if (result.claim) graph.writes += 1;
    return result;
}

function recordClaim(graph, claim) {
    if (!claim || !CLAIM_KINDS.includes(claim.kind) || !claim.statement) return { added: false, status: "rejected", reason: "claim needs a kind and a statement" };
    const verdict = evaluateClaim(graph, claim);
    const record = {
        id: claim.id || `c${graph.claims.length + 1}`,
        kind: claim.kind,
        subject: claim.subject || null,
        // Long enough for any one-sentence claim; the cut is a guard against
        // a runaway statement, not a display limit.
        statement: String(claim.statement).slice(0, 2000),
        evidence: [...new Set(claim.evidence || [])],
        status: verdict.ok ? "supported" : "insufficient",
        reason: verdict.reason,
        // Whether the claim answers the question that was asked or only
        // describes the anchor; the model's own claims answer by construction.
        bearing: claim.bearing === "context" ? "context" : "answers",
        source: claim.source || null,
        // When it was made, for the readers that show claims as comments.
        at: Number.isFinite(claim.at) ? claim.at : Date.now(),
        seq: graph.writes,
    };
    const existing = graph.claims.findIndex((c) => c.id === record.id);
    if (existing >= 0) graph.claims[existing] = record; else graph.claims.push(record);
    return { added: existing < 0, status: record.status, reason: record.reason, claim: record };
}

// Compact text the model reads each turn. Grouped by kind, evidence marked,
// bounded by a character budget with counts for what was cut.
const EVIDENCE_MARK = { execution: "E", conversation: "C", "prompt-summary": "P", directory: "D", derived: "d" };

export function renderGraph(graph, { budget = 6000 } = {}) {
    // An empty graph is stated, not omitted: the model then knows there is
    // nothing to read before its first query instead of asking graph_state.
    if (!graph.nodes.size && !graph.claims.length) {
        return ["<investigation_graph>", "empty: no query has run in this chat yet, so graph_state has nothing to show. Start with a query; from then on each result's _graph line and this block say what is held.", "</investigation_graph>"].join("\n");
    }
    const lines = ["<investigation_graph>", "Established so far. Marks: E execution record, C conversation capture, P prompt summary, D directory, d derived. Cite node keys in `claim`."];
    for (const kind of NODE_KINDS) {
        const nodes = [...graph.nodes.values()].filter((n) => n.kind === kind);
        if (!nodes.length) continue;
        lines.push(`${kind} (${nodes.length}):`);
        for (const n of nodes) {
            const marks = [...n.evidence].map((e) => EVIDENCE_MARK[e] || "?").sort().join("");
            const rels = [...graph.edges.values()].filter((e) => e.from === n.key).map((e) => `${e.kind} ${e.to}`).slice(0, 4);
            lines.push(`  - ${n.key}${n.label ? ` "${n.label.slice(0, 80)}"` : ""} [${marks}]${rels.length ? " " + rels.join("; ") : ""}`);
        }
    }
    if (graph.claims.length) {
        lines.push("claims:");
        for (const c of graph.claims) lines.push(`  - ${c.id} ${c.kind} ${c.status}${c.bearing === "context" ? " [context]" : ""}: ${c.statement}${c.status === "insufficient" ? ` (${c.reason})` : ""}`);
    }
    lines.push("</investigation_graph>");
    let text = lines.join("\n");
    if (text.length > budget) {
        const kept = [];
        let used = 0;
        let cut = 0;
        for (const line of lines) {
            if (used + line.length + 1 > budget - 80 && !/^<|^claims:|^  - c\d/.test(line)) { cut++; continue; }
            kept.push(line);
            used += line.length + 1;
        }
        kept.splice(kept.length - 1, 0, `  ... ${cut} more lines held back; query the graph for the rest.`);
        text = kept.join("\n");
    }
    return text;
}

export function toJSON(graph) {
    return {
        nodes: [...graph.nodes.values()].map((n) => ({ ...n, evidence: [...n.evidence] })),
        edges: [...graph.edges.values()].map((e) => ({ ...e, evidence: [...e.evidence] })),
        claims: graph.claims,
        writes: graph.writes,
    };
}

export function fromJSON(data) {
    const graph = createGraph();
    for (const n of data?.nodes || []) graph.nodes.set(n.key, { ...n, evidence: new Set(n.evidence || []), sources: n.sources || [], attrs: n.attrs || {} });
    for (const e of data?.edges || []) graph.edges.set(e.key, { ...e, evidence: new Set(e.evidence || []), sources: e.sources || [] });
    graph.claims = Array.isArray(data?.claims) ? data.claims.slice() : [];
    graph.writes = data?.writes || 0;
    return graph;
}

// Queries the model or a leaf can run without touching the API.
export function nodesOf(graph, kind, { evidence = null } = {}) {
    return [...graph.nodes.values()].filter((n) => n.kind === kind && (!evidence || n.evidence.has(evidence)));
}

export function neighbours(graph, key) {
    const out = [];
    for (const e of graph.edges.values()) {
        if (e.from === key && graph.nodes.has(e.to)) out.push({ via: e.kind, node: graph.nodes.get(e.to) });
        if (e.to === key && graph.nodes.has(e.from)) out.push({ via: e.kind, node: graph.nodes.get(e.from), reverse: true });
    }
    return out;
}

// The slice a leaf gets: one anchor, its neighbours, and the claims that
// cite it. Small by construction so a leaf's prompt stays small.
export function sliceAround(graph, key, { hops = 1 } = {}) {
    const keys = new Set([key]);
    let frontier = [key];
    for (let h = 0; h < hops; h++) {
        const next = [];
        for (const k of frontier) for (const nb of neighbours(graph, k)) if (!keys.has(nb.node.key)) { keys.add(nb.node.key); next.push(nb.node.key); }
        frontier = next;
    }
    const sub = createGraph();
    for (const k of keys) if (graph.nodes.has(k)) sub.nodes.set(k, graph.nodes.get(k));
    for (const e of graph.edges.values()) if (keys.has(e.from) && keys.has(e.to)) sub.edges.set(e.key, e);
    sub.claims = graph.claims.filter((c) => c.evidence.some((k) => keys.has(k)) || c.subject === key);
    return sub;
}
